import type { EcosystemId } from './ecosystems';
import { applyEcosystemReadmePlaceholders, type ParsedEcosystemReadme } from './ecosystem-readme';

export type EcosystemPatchStyleId = string;

export type EcosystemPatchTarget = {
    patch_style: EcosystemPatchStyleId;
    file?: string;
    variable?: string;
    cache_type?: string;
    cache_description?: string;
    value_true?: string | number;
    value_false?: string | number;
    attribute?: string;
    dimension?: 'width' | 'height';
};

export type EcosystemSelectOption = {
    id: string;
    label: string;
    description?: string;
};

export type EcosystemParameterUiMode = 'hidden' | 'locked';

export type EcosystemBoardDetail = {
    label: string;
    value: string;
};

export type EcosystemBoardDefinition = {
    id: string;
    label: string;
    description?: string;
    horizontal_resolution: number;
    vertical_resolution: number;
    build_command: string;
    details?: EcosystemBoardDetail[];
};

export type EcosystemParameterDefinition = {
    id: string;
    label: string;
    tooltip?: string;
    type: 'bool' | 'number' | 'select' | 'board';
    default: boolean | number | string;
    min?: number;
    max?: number;
    options?: EcosystemSelectOption[];
    options_for_value?: Record<string, Record<string, EcosystemSelectOption[]>>;
    targets?: EcosystemPatchTarget[];
    suggests?: string[];
    enables?: string[];
    managed_by?: string;
    requires_bundle?: boolean;
    ui?: EcosystemParameterUiMode;
};

export type EcosystemParameterGroupLayout = 'display_size';

export type EcosystemParameterGroup = {
    id: string;
    label: string;
    tooltip?: string;
    layout?: EcosystemParameterGroupLayout;
    bundle?: string;
    parameters: EcosystemParameterDefinition[];
};

export type EcosystemBundleDefinition = {
    id: string;
    label: string;
    tooltip?: string;
    enables: string[];
    locked?: string[];
};

export type EcosystemPatchStyleDefinition = {
    description?: string;
    file?: string;
    replace_pattern?: string;
    replace_template?: string;
};

export type EcosystemProjectConfig = {
    version: string;
    patch_styles: Record<EcosystemPatchStyleId, EcosystemPatchStyleDefinition>;
    boards?: EcosystemBoardDefinition[];
    ui?: {
        fields: EcosystemParameterDefinition[];
    };
    groups: EcosystemParameterGroup[];
    bundles?: EcosystemBundleDefinition[];
};

export type EcosystemParameterValues = Record<string, boolean | number | string>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const parsePatchTarget = (value: unknown): EcosystemPatchTarget | null => {
    if (!isRecord(value) || typeof value.patch_style !== 'string') return null;
    return value as EcosystemPatchTarget;
};

const parseSelectOption = (value: unknown): EcosystemSelectOption | null => {
    if (!isRecord(value) || typeof value.id !== 'string' || typeof value.label !== 'string') return null;
    return {
        id: value.id,
        label: value.label,
        description: typeof value.description === 'string' ? value.description : undefined
    };
};

const parseOptionsForValue = (value: unknown): Record<string, Record<string, EcosystemSelectOption[]>> | undefined => {
    if (!isRecord(value)) return undefined;

    const result: Record<string, Record<string, EcosystemSelectOption[]>> = {};
    for (const [fieldId, mapping] of Object.entries(value)) {
        if (!isRecord(mapping)) continue;
        result[fieldId] = {};
        for (const [fieldValue, options] of Object.entries(mapping)) {
            if (!Array.isArray(options)) continue;
            const parsed: EcosystemSelectOption[] = options
                .map(parseSelectOption)
                .filter((option): option is EcosystemSelectOption => option !== null);
            if (parsed.length > 0) result[fieldId][fieldValue] = parsed;
        }
    }

    return Object.keys(result).length > 0 ? result : undefined;
};

const parseBoard = (value: unknown): EcosystemBoardDefinition | null => {
    if (!isRecord(value) || typeof value.id !== 'string' || typeof value.label !== 'string') return null;
    if (typeof value.build_command !== 'string') return null;

    const horizontalResolution: number = Number(value.horizontal_resolution);
    const verticalResolution: number = Number(value.vertical_resolution);
    if (!Number.isFinite(horizontalResolution) || !Number.isFinite(verticalResolution)) return null;
    if (horizontalResolution <= 0 || verticalResolution <= 0) return null;

    const details: EcosystemBoardDetail[] | undefined = Array.isArray(value.details)
        ? value.details
              .map((detail): EcosystemBoardDetail | null => {
                  if (!isRecord(detail) || typeof detail.label !== 'string' || typeof detail.value !== 'string') {
                      return null;
                  }
                  return { label: detail.label, value: detail.value };
              })
              .filter((detail): detail is EcosystemBoardDetail => detail !== null)
        : undefined;

    return {
        id: value.id,
        label: value.label,
        description: typeof value.description === 'string' ? value.description : undefined,
        horizontal_resolution: horizontalResolution,
        vertical_resolution: verticalResolution,
        build_command: value.build_command,
        details: details && details.length > 0 ? details : undefined
    };
};

const parseParameter = (value: unknown): EcosystemParameterDefinition | null => {
    if (!isRecord(value) || typeof value.id !== 'string' || typeof value.label !== 'string') return null;
    if (value.type !== 'bool' && value.type !== 'number' && value.type !== 'select' && value.type !== 'board')
        return null;
    if (value.default === undefined) return null;

    const legacyTarget: EcosystemPatchTarget | null = parsePatchTarget(value.target);
    const targets: EcosystemPatchTarget[] = Array.isArray(value.targets)
        ? value.targets.map(parsePatchTarget).filter((target): target is EcosystemPatchTarget => target !== null)
        : legacyTarget
          ? [legacyTarget]
          : [];

    const options: EcosystemSelectOption[] | undefined = Array.isArray(value.options)
        ? value.options.map(parseSelectOption).filter((option): option is EcosystemSelectOption => option !== null)
        : undefined;

    return {
        id: value.id,
        label: value.label,
        tooltip: typeof value.tooltip === 'string' ? value.tooltip : undefined,
        type: value.type,
        default: value.default as boolean | number | string,
        min: typeof value.min === 'number' ? value.min : undefined,
        max: typeof value.max === 'number' ? value.max : undefined,
        options: options && options.length > 0 ? options : undefined,
        options_for_value: parseOptionsForValue(value.options_for_value),
        targets: targets.length > 0 ? targets : undefined,
        suggests: Array.isArray(value.suggests)
            ? value.suggests.filter((item): item is string => typeof item === 'string')
            : undefined,
        enables: Array.isArray(value.enables)
            ? value.enables.filter((item): item is string => typeof item === 'string')
            : undefined,
        managed_by: typeof value.managed_by === 'string' ? value.managed_by : undefined,
        requires_bundle: value.requires_bundle === true ? true : undefined,
        ui: value.ui === 'hidden' || value.ui === 'locked' ? value.ui : undefined
    };
};

const parseGroup = (value: unknown): EcosystemParameterGroup | null => {
    if (!isRecord(value) || typeof value.id !== 'string' || typeof value.label !== 'string') return null;
    if (!Array.isArray(value.parameters)) return null;

    const parameters: EcosystemParameterDefinition[] = value.parameters
        .map(parseParameter)
        .filter((parameter): parameter is EcosystemParameterDefinition => parameter !== null);

    return parameters.length > 0
        ? {
              id: value.id,
              label: value.label,
              tooltip: typeof value.tooltip === 'string' ? value.tooltip : undefined,
              layout: value.layout === 'display_size' ? 'display_size' : undefined,
              bundle: typeof value.bundle === 'string' ? value.bundle : undefined,
              parameters
          }
        : null;
};

const parseBundle = (value: unknown): EcosystemBundleDefinition | null => {
    if (!isRecord(value) || typeof value.id !== 'string' || typeof value.label !== 'string') return null;
    if (!Array.isArray(value.enables)) return null;

    return {
        id: value.id,
        label: value.label,
        tooltip: typeof value.tooltip === 'string' ? value.tooltip : undefined,
        enables: value.enables.filter((item): item is string => typeof item === 'string'),
        locked: Array.isArray(value.locked)
            ? value.locked.filter((item): item is string => typeof item === 'string')
            : undefined
    };
};

export const parseEcosystemProjectConfig = (raw: string): EcosystemProjectConfig | null => {
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return null;
    }

    if (!isRecord(parsed) || typeof parsed.version !== 'string' || !Array.isArray(parsed.groups)) return null;
    if (!isRecord(parsed.patch_styles)) return null;

    const groups: EcosystemParameterGroup[] = parsed.groups
        .map(parseGroup)
        .filter((group): group is EcosystemParameterGroup => group !== null);

    const uiFields: EcosystemParameterDefinition[] | undefined =
        isRecord(parsed.ui) && Array.isArray(parsed.ui.fields)
            ? parsed.ui.fields
                  .map(parseParameter)
                  .filter((field): field is EcosystemParameterDefinition => field !== null)
            : undefined;

    const boards: EcosystemBoardDefinition[] | undefined = Array.isArray(parsed.boards)
        ? parsed.boards.map(parseBoard).filter((board): board is EcosystemBoardDefinition => board !== null)
        : undefined;

    if (groups.length === 0 && (!uiFields || uiFields.length === 0)) return null;

    const bundles: EcosystemBundleDefinition[] | undefined = Array.isArray(parsed.bundles)
        ? parsed.bundles.map(parseBundle).filter((bundle): bundle is EcosystemBundleDefinition => bundle !== null)
        : undefined;

    return {
        version: parsed.version,
        patch_styles: parsed.patch_styles as Record<EcosystemPatchStyleId, EcosystemPatchStyleDefinition>,
        boards: boards && boards.length > 0 ? boards : undefined,
        ui: uiFields && uiFields.length > 0 ? { fields: uiFields } : undefined,
        groups,
        bundles: bundles && bundles.length > 0 ? bundles : undefined
    };
};

export const isDisplaySizeGroup = (group: EcosystemParameterGroup): boolean => {
    if (group.layout === 'display_size') return true;

    const parameterIds = new Set(group.parameters.map((parameter) => parameter.id));
    return group.id === 'resolution' && parameterIds.has('LV_HOR_RES') && parameterIds.has('LV_VER_RES');
};

export const getDisplaySizeParameters = (
    group: EcosystemParameterGroup
): { width: EcosystemParameterDefinition; height: EcosystemParameterDefinition } | null => {
    if (!isDisplaySizeGroup(group)) return null;

    const width: EcosystemParameterDefinition | undefined = group.parameters.find(
        (parameter) => parameter.id === 'LV_HOR_RES' && parameter.type === 'number'
    );
    const height: EcosystemParameterDefinition | undefined = group.parameters.find(
        (parameter) => parameter.id === 'LV_VER_RES' && parameter.type === 'number'
    );

    return width && height ? { width, height } : null;
};

export const clampNumberParameter = (value: number, parameter: EcosystemParameterDefinition): number => {
    let next: number = Math.round(value);
    if (parameter.min !== undefined) next = Math.max(parameter.min, next);
    if (parameter.max !== undefined) next = Math.min(parameter.max, next);
    return next;
};

export const getEcosystemProjectConfigPathSegment = (ecosystemId: EcosystemId): string =>
    `ecosystems/${ecosystemId}/project.json`;

export const collectEcosystemParameters = (config: EcosystemProjectConfig): EcosystemParameterDefinition[] => {
    const parameters: EcosystemParameterDefinition[] = [];
    const seen = new Set<string>();

    const addParameter = (parameter: EcosystemParameterDefinition): void => {
        if (seen.has(parameter.id)) return;
        seen.add(parameter.id);
        parameters.push(parameter);
    };

    config.ui?.fields.forEach(addParameter);
    config.groups.forEach((group) => group.parameters.forEach(addParameter));
    return parameters;
};

export const getDefaultEcosystemParameterValues = (config: EcosystemProjectConfig): EcosystemParameterValues => {
    const values: EcosystemParameterValues = {};
    for (const parameter of collectEcosystemParameters(config)) {
        values[parameter.id] = parameter.default;
    }
    for (const bundle of config.bundles ?? []) {
        values[bundle.id] = false;
    }
    return values;
};

export const getBoardById = (config: EcosystemProjectConfig, boardId: string): EcosystemBoardDefinition | undefined =>
    config.boards?.find((board) => board.id === boardId);

export const resolveEcosystemReadmeForDisplay = (
    readme: ParsedEcosystemReadme,
    config: EcosystemProjectConfig,
    values: EcosystemParameterValues
): ParsedEcosystemReadme => {
    const boardParameter: EcosystemParameterDefinition | undefined = collectEcosystemParameters(config).find(
        (parameter) => parameter.type === 'board'
    );
    if (!boardParameter) return readme;

    const boardId: string = String(values[boardParameter.id] ?? boardParameter.default);
    const board: EcosystemBoardDefinition | undefined = getBoardById(config, boardId);
    if (!board?.build_command) return readme;

    return applyEcosystemReadmePlaceholders(readme, { BUILD_COMMAND: board.build_command });
};

export const formatBoardOptionDescription = (board: EcosystemBoardDefinition): string =>
    getBoardDescriptionRows(board)
        .map((row) => `${row.label}: ${row.value}`)
        .join('\n');

export const getBoardDescriptionRows = (board: EcosystemBoardDefinition): EcosystemBoardDetail[] => [
    { label: 'Screen size', value: `${board.horizontal_resolution} × ${board.vertical_resolution}` },
    ...(board.details ?? [])
];

export const getSelectOptionsForParameter = (
    parameter: EcosystemParameterDefinition,
    values: EcosystemParameterValues,
    config?: EcosystemProjectConfig
): EcosystemSelectOption[] => {
    if (parameter.type === 'board') {
        return (config?.boards ?? []).map((board) => ({
            id: board.id,
            label: board.label,
            description: formatBoardOptionDescription(board)
        }));
    }

    if (!parameter.options_for_value) return parameter.options ?? [];

    for (const [fieldId, mapping] of Object.entries(parameter.options_for_value)) {
        const fieldValue: string = String(values[fieldId] ?? '');
        const mapped: EcosystemSelectOption[] | undefined = mapping[fieldValue];
        if (mapped && mapped.length > 0) return mapped;
    }

    return parameter.options ?? [];
};

export const applyManagedParameterValues = (
    config: EcosystemProjectConfig,
    values: EcosystemParameterValues
): EcosystemParameterValues => {
    const next: EcosystemParameterValues = { ...values };
    const parameters: EcosystemParameterDefinition[] = collectEcosystemParameters(config);
    const parameterById = new Map(parameters.map((parameter) => [parameter.id, parameter]));

    for (const parameter of parameters) {
        if (!parameter.managed_by) continue;
        const managerValue = next[parameter.managed_by];
        if (parameter.type === 'bool' && typeof managerValue === 'boolean') {
            next[parameter.id] = managerValue;
        }
    }

    for (const bundle of config.bundles ?? []) {
        if (!next[bundle.id]) continue;
        for (const parameterId of bundle.enables) {
            const parameter = parameterById.get(parameterId);
            if (parameter?.type === 'bool') next[parameterId] = true;
        }
        for (const parameterId of bundle.locked ?? []) {
            const parameter = parameterById.get(parameterId);
            if (parameter?.type === 'bool') next[parameterId] = false;
        }
    }

    for (const group of config.groups) {
        if (!group.bundle || next[group.bundle]) continue;
        for (const parameter of group.parameters) {
            if (!parameter.requires_bundle) continue;
            next[parameter.id] = parameter.default as boolean | number | string;
        }
    }

    return next;
};

export const validateEcosystemParameterValues = (
    config: EcosystemProjectConfig,
    values: EcosystemParameterValues
): boolean => {
    const resolved: EcosystemParameterValues = applyManagedParameterValues(config, values);

    for (const parameter of collectEcosystemParameters(config)) {
        if (parameter.ui === 'hidden') continue;
        const value = resolved[parameter.id];

        if (parameter.type === 'number') {
            const numeric: number = Number(value);
            if (!Number.isFinite(numeric) || !Number.isInteger(numeric)) return false;
            if (parameter.min !== undefined && numeric < parameter.min) return false;
            if (parameter.max !== undefined && numeric > parameter.max) return false;
            if (numeric <= 0) return false;
            continue;
        }

        if (parameter.type === 'bool') {
            if (typeof value !== 'boolean') return false;
            continue;
        }

        if (parameter.type === 'select' || parameter.type === 'board') {
            const options: EcosystemSelectOption[] = getSelectOptionsForParameter(parameter, resolved, config);
            if (!options.some((option) => option.id === value)) return false;
        }
    }

    return true;
};

const formatPatchTemplate = (template: string, replacements: Record<string, string>): string =>
    template.replace(/\{(\w+)\}/g, (_match, key: string) => replacements[key] ?? '');

const applyLvConfDefaultsPatch = (
    content: string,
    target: EcosystemPatchTarget,
    value: boolean | number | string,
    style: EcosystemPatchStyleDefinition
): string => {
    const variable: string = target.variable ?? '';
    if (!variable) return content;

    const resolvedValue: string | number =
        typeof value === 'boolean' ? (value ? (target.value_true ?? 1) : (target.value_false ?? 0)) : value;

    const patternSource: string = style.replace_pattern?.replace(/\{variable\}/g, variable) ?? `^${variable}\\s+\\S+`;
    const pattern: RegExp = new RegExp(patternSource, 'm');
    const replacement: string = formatPatchTemplate(style.replace_template ?? '{variable} {value}', {
        variable,
        value: String(resolvedValue)
    });

    if (!pattern.test(content)) return content;
    return content.replace(pattern, replacement);
};

const applyCmakeSetPatch = (
    content: string,
    target: EcosystemPatchTarget,
    value: boolean | number | string
): string => {
    const variable: string = target.variable ?? '';
    if (!variable) return content;

    const pattern: RegExp = new RegExp(`set\\(${variable}\\s+[\\s\\S]*?CACHE\\s+\\S+\\s+"[^"]*"\\)`, 'm');
    const replacement: string = `set(${variable}\n    ${String(value)}\n    CACHE ${target.cache_type ?? 'STRING'} "${target.cache_description ?? ''}")`;
    if (!pattern.test(content)) return content;
    return content.replace(pattern, replacement);
};

const applyProjectXmlAttributePatch = (
    content: string,
    target: EcosystemPatchTarget,
    value: boolean | number | string
): string => {
    const attribute: string = target.attribute ?? '';
    if (!attribute) return content;

    const attributeValue: string = String(value);
    if (new RegExp(`\\b${attribute}\\s*=`).test(content)) {
        return content.replace(
            new RegExp(`\\b${attribute}\\s*=\\s*["'][^"']*["']`),
            `${attribute}="${attributeValue}"`
        );
    }

    return content.replace(/<project\b/, `<project ${attribute}="${attributeValue}"`);
};

const applyProjectXmlDisplayPatch = (
    content: string,
    target: EcosystemPatchTarget,
    value: boolean | number | string
): string => {
    const dimension: 'width' | 'height' | undefined = target.dimension;
    if (!dimension) return content;

    return content.replace(/<display\b([^>]*)>/, (_match, attrs: string) => {
        let updatedAttrs: string = attrs;
        if (new RegExp(`\\b${dimension}\\s*=`).test(updatedAttrs)) {
            updatedAttrs = updatedAttrs.replace(
                new RegExp(`\\b${dimension}\\s*=\\s*["'][^"']*["']`),
                `${dimension}="${String(value)}"`
            );
        } else {
            updatedAttrs = `${updatedAttrs} ${dimension}="${String(value)}"`;
        }
        return `<display${updatedAttrs}>`;
    });
};

const applyMainCHalInitPatch = (
    content: string,
    target: EcosystemPatchTarget,
    value: boolean | number | string
): string => {
    const dimension: 'width' | 'height' | undefined = target.dimension;
    if (!dimension) return content;

    return content.replace(/hal_init\s*\(\s*(\d+)\s*,\s*(\d+)\s*\)/, (_match, width: string, height: string) => {
        if (dimension === 'width') return `hal_init(${String(value)}, ${height})`;
        return `hal_init(${width}, ${String(value)})`;
    });
};

const applyConfiguredPatch = (
    content: string,
    target: EcosystemPatchTarget,
    value: boolean | number | string,
    style: EcosystemPatchStyleDefinition
): string => {
    switch (target.patch_style) {
        case 'lv_conf_defaults':
            return applyLvConfDefaultsPatch(content, target, value, style);
        case 'cmake_set':
            return applyCmakeSetPatch(content, target, value);
        case 'project_xml_attribute':
            return applyProjectXmlAttributePatch(content, target, value);
        case 'project_xml_display':
            return applyProjectXmlDisplayPatch(content, target, value);
        case 'main_c_hal_init':
            return applyMainCHalInitPatch(content, target, value);
        default: {
            if (!style.replace_pattern || !style.replace_template) return content;
            const replacements: Record<string, string> = {
                variable: target.variable ?? '',
                value: String(
                    typeof value === 'boolean'
                        ? value
                            ? String(target.value_true ?? 1)
                            : String(target.value_false ?? 0)
                        : value
                ),
                cache_type: target.cache_type ?? 'STRING',
                cache_description: target.cache_description ?? ''
            };
            const pattern: RegExp = new RegExp(formatPatchTemplate(style.replace_pattern, replacements), 'm');
            const replacement: string = formatPatchTemplate(style.replace_template, replacements);
            if (!pattern.test(content)) return content;
            return content.replace(pattern, replacement);
        }
    }
};

export type EcosystemPatchFileIO = {
    readFile: (filePath: string) => Promise<string>;
    writeFile: (filePath: string, content: string) => Promise<void>;
};

/** Apply ecosystem project.json parameter patches to files under the created project directory. */
export const applyEcosystemProjectPatches = async (
    projectPath: string,
    config: EcosystemProjectConfig,
    values: EcosystemParameterValues,
    io: EcosystemPatchFileIO
): Promise<void> => {
    const resolvedValues: EcosystemParameterValues = applyManagedParameterValues(config, values);
    const fileContents = new Map<string, string>();

    const readFile = async (relativeFilePath: string): Promise<string> => {
        const normalizedPath: string = relativeFilePath.replace(/\\/g, '/');
        const cached: string | undefined = fileContents.get(normalizedPath);
        if (cached !== undefined) return cached;

        const absolutePath: string = `${projectPath.replace(/\\/g, '/').replace(/\/+$/, '')}/${normalizedPath}`;
        const content: string = await io.readFile(absolutePath);
        fileContents.set(normalizedPath, content);
        return content;
    };

    const writeFile = async (relativeFilePath: string, content: string): Promise<void> => {
        const normalizedPath: string = relativeFilePath.replace(/\\/g, '/');
        fileContents.set(normalizedPath, content);
        const absolutePath: string = `${projectPath.replace(/\\/g, '/').replace(/\/+$/, '')}/${normalizedPath}`;
        await io.writeFile(absolutePath, content);
    };

    for (const parameter of collectEcosystemParameters(config)) {
        if (parameter.type === 'board') {
            const board: EcosystemBoardDefinition | undefined = getBoardById(
                config,
                String(resolvedValues[parameter.id])
            );
            if (!board) continue;

            const projectXmlPath: string = (config.patch_styles.project_xml_display?.file ?? 'ui/project.xml').replace(
                /\\/g,
                '/'
            );
            let projectXml: string = await readFile(projectXmlPath);
            projectXml = applyProjectXmlDisplayPatch(
                projectXml,
                { patch_style: 'project_xml_display', dimension: 'width' },
                board.horizontal_resolution
            );
            projectXml = applyProjectXmlDisplayPatch(
                projectXml,
                { patch_style: 'project_xml_display', dimension: 'height' },
                board.vertical_resolution
            );
            await writeFile(projectXmlPath, projectXml);

            const readmePath: string = (config.patch_styles.readme_placeholder?.file ?? 'README.md').replace(
                /\\/g,
                '/'
            );
            const readmeContent: string = await readFile(readmePath);
            await writeFile(readmePath, readmeContent.replace(/\{\{BUILD_COMMAND\}\}/g, board.build_command));
            continue;
        }

        if (!parameter.targets || parameter.targets.length === 0) continue;
        const value = resolvedValues[parameter.id];
        if (value === undefined) continue;

        for (const target of parameter.targets) {
            const style: EcosystemPatchStyleDefinition = config.patch_styles[target.patch_style] ?? {};

            const relativeFilePath: string = (target.file ?? style.file ?? '').replace(/\\/g, '/');
            if (!relativeFilePath) continue;

            const currentContent: string = await readFile(relativeFilePath);
            const patchedContent: string = applyConfiguredPatch(currentContent, target, value, style);
            await writeFile(relativeFilePath, patchedContent);
        }
    }
};
